import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabase } from '../../../lib/supabase';
import ServiceZonesModal from './ServiceZonesModal';
import { customerPortalService } from '../../../services/customerPortalService';
import EmailExistsModal from './EmailExistsModal';
import StepActions from './StepActions';
import { cn } from '../../../utils/cn';
import { getCustomerInfoStepSubmitLabel, isFixedPriceService, isQuoteService } from '../servicePricing';

const FIELD_ERROR_CLASS =
  'border-orange-500 focus-visible:border-orange-500 focus-visible:ring-orange-500/30 hover:border-orange-500';

const MIN_ADDRESS_QUERY_LENGTH = 2;
const ADDRESS_SEARCH_DEBOUNCE_MS = 200;

/** Numéro de rue en tête de saisie (ex. "12", "12bis", "12 B"). */
const extractLeadingHouseNumber = (rawQuery) => {
  const match = `${rawQuery || ''}`.trim().match(/^(\d+[a-zA-Z]?)\b/i);
  return match ? match[1].toLowerCase() : null;
};

const featureHasMatchingHouseNumber = (feature, houseNumber) => {
  if (!houseNumber) return true;
  const hn = `${feature?.properties?.housenumber || ''}`.trim().toLowerCase();
  if (!hn) return false;
  return hn === houseNumber || hn.startsWith(houseNumber);
};

const buildAddressSearchQueries = (rawQuery) => {
  const query = rawQuery.trim();
  if (!query) return [];
  // Toujours chercher avec la saisie complète (numéro inclus).
  // Ne plus relancer une recherche "sans numéro" : elle remonte des rues
  // sans housenumber en tête de liste.
  return [query];
};

const getAddressSearchCities = (allowedZones, contextCity) => {
  if (contextCity?.trim()) {
    return [contextCity.trim()];
  }

  const cities = [...new Set(allowedZones.map((zone) => zone.municipality_name).filter(Boolean))];
  if (cities.length === 0) return [];

  if (cities.includes('Paris')) {
    return ['Paris', ...cities.filter((city) => city !== 'Paris')].slice(0, 4);
  }

  return cities.slice(0, 4);
};

const isPostcodeInServiceZones = (postcode, allowedPostcodes) => {
  const normalized = `${postcode || ''}`.trim();
  if (!normalized) return false;
  if (allowedPostcodes.includes(normalized)) return true;

  // Paris : certains résultats API renvoient 751XX au lieu de 750XX
  if (normalized.startsWith('751')) {
    return allowedPostcodes.includes(`750${normalized.slice(3)}`);
  }
  if (normalized.startsWith('750')) {
    return allowedPostcodes.includes(`751${normalized.slice(3)}`);
  }

  return false;
};

const fetchAddressFeatures = async (searchQuery, city, { requireHouseNumber = false } = {}) => {
  const params = new URLSearchParams({
    q: searchQuery,
    limit: '20',
    autocomplete: '1',
  });

  if (city) {
    params.set('city', city);
  }

  // Quand un numéro + rue sont saisis, privilégier les adresses au numéro
  if (requireHouseNumber && /[a-zA-ZÀ-ÿ]/.test(searchQuery)) {
    params.set('type', 'housenumber');
  }

  const res = await fetch(`https://api-adresse.data.gouv.fr/search/?${params.toString()}`);
  if (!res.ok) return [];

  const json = await res.json();
  return json.features || [];
};

const searchAddress = async (query, { allowedZones = [], contextCity = '' } = {}) => {
  const queries = buildAddressSearchQueries(query);
  if (queries.length === 0) return [];

  const houseNumber = extractLeadingHouseNumber(query);
  const allowedPostcodes = allowedZones
    .map((zone) => zone.postal_code)
    .filter(Boolean);
  const cities = getAddressSearchCities(allowedZones, contextCity);
  const searchCities = cities.length > 0 ? cities : [''];
  const seen = new Set();
  const features = [];

  const countAllowedMatches = () => {
    const scoped = houseNumber
      ? features.filter((feature) => featureHasMatchingHouseNumber(feature, houseNumber))
      : features;

    return allowedPostcodes.length === 0
      ? scoped.length
      : scoped.filter((feature) =>
          isPostcodeInServiceZones(feature.properties.postcode, allowedPostcodes)
        ).length;
  };

  for (const city of searchCities) {
    for (const searchQuery of queries) {
      const batch = await fetchAddressFeatures(searchQuery, city, {
        requireHouseNumber: Boolean(houseNumber),
      });
      mergeAddressFeatures(batch, seen, features);

      if (countAllowedMatches() >= 5) {
        break;
      }
    }

    if (countAllowedMatches() >= 5) {
      break;
    }
  }

  let results = features;
  if (houseNumber) {
    results = results.filter((feature) =>
      featureHasMatchingHouseNumber(feature, houseNumber)
    );
  }

  if (allowedPostcodes.length === 0) {
    return results.slice(0, 10);
  }

  return results
    .filter((feature) => isPostcodeInServiceZones(feature.properties.postcode, allowedPostcodes))
    .slice(0, 10);
};

const mergeAddressFeatures = (incoming, seen, target) => {
  incoming.forEach((feature) => {
    const key = feature.properties?.id || feature.properties?.label;
    if (!key || seen.has(key)) return;
    seen.add(key);
    target.push(feature);
  });
  return target;
};

const fetchAllowedPostcodes = async () => {
  try {
    const { data, error } = await supabase
      .from('service_zones')
      .select('*')
      .eq('is_active', true)
      .order('postal_code');
    console.log('Zones d\'intervention chargées :', data);
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('Erreur lors du chargement des codes postaux:', err);
    return [];
  }
};

const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  error,
  highlightError,
  onBlur,
  allowedZones = [],
  contextCity = '',
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const timer = useRef(null);
  const requestId = useRef(0);
  const wrapperRef = useRef(null);

  const runSearch = async (val) => {
    const trimmed = val.trim();
    // BAN renvoie 400 si la requête est trop courte / numéro seul
    const hasStreetText = /[a-zA-ZÀ-ÿ]/.test(trimmed);
    if (trimmed.length < MIN_ADDRESS_QUERY_LENGTH || !hasStreetText) {
      setSuggestions([]);
      setOpen(false);
      setIsLoading(false);
      return;
    }

    const currentRequest = ++requestId.current;
    setIsLoading(true);

    try {
      const results = await searchAddress(val, {
        allowedZones,
        contextCity,
      });

      if (currentRequest !== requestId.current) return;

      setSuggestions(results);
      setOpen(results.length > 0);
    } catch (searchError) {
      if (currentRequest !== requestId.current) return;
      console.error('Erreur lors de la recherche d\'adresse:', searchError);
      setSuggestions([]);
      setOpen(false);
    } finally {
      if (currentRequest === requestId.current) {
        setIsLoading(false);
      }
    }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);

    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      runSearch(val);
    }, ADDRESS_SEARCH_DEBOUNCE_MS);
  };

  const handleSelect = (feature) => {
    const props = feature.properties;
    const coords = feature.geometry.coordinates;
    onSelect({
      streetLine: props.name || props.label || '',
      postcode: props.postcode || '',
      city: props.city || props.municipality || props.town || '',
      lat: coords?.[1] ?? null,
      lng: coords?.[0] ?? null,
    });
    setOpen(false);
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Ne pas ouvrir les suggestions au chargement (ex. adresse préremplie + zones).
  // Rafraîchir uniquement si la liste est déjà ouverte.
  useEffect(() => {
    if (!open) return;
    if (value.trim().length < MIN_ADDRESS_QUERY_LENGTH) return;
    runSearch(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recherche manuelle via focus/saisie
  }, [allowedZones, contextCity]);

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        label="Numéro et type de voie"
        type="text"
        placeholder="Ex : 12 rue, 5 avenue, 3 boulevard"
        value={value}
        onChange={handleChange}
        onFocus={() => {
          // Rouvrir la liste seulement si une recherche a déjà été faite (saisie utilisateur)
          if (suggestions.length > 0 && value.trim().length >= MIN_ADDRESS_QUERY_LENGTH) {
            setOpen(true);
          }
        }}
        onBlur={onBlur}
        error={error}
        required
        icon="MapPin"
        className={highlightError ? FIELD_ERROR_CLASS : undefined}
        autoComplete="off"
      />
      {isLoading && (
        <p className="absolute z-50 left-0 right-0 mt-1 px-4 py-2 text-sm text-muted-foreground bg-card border border-border rounded-lg shadow-lg">
          Recherche d&apos;adresses...
        </p>
      )}
      {open && !isLoading && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto">
          {suggestions.map((f) => (
            <li
              key={f.properties.id}
              className="px-4 py-2.5 text-sm cursor-pointer hover:bg-primary/10 flex items-center gap-2 transition-colors border-b border-border last:border-0"
              onMouseDown={() => handleSelect(f)}
            >
              <Icon name="MapPin" size={14} color="var(--color-primary)" className="flex-shrink-0" />
              <span className="flex-1">{f.properties.label}</span>
              {f.properties.postcode && (
                <span className="text-xs text-muted-foreground font-mono">
                  {f.properties.postcode}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const getFullNameFromData = (data) => {
  if (data?.fullName?.trim()) return data.fullName.trim();
  return `${data?.firstName || ''} ${data?.lastName || ''}`.trim();
};

const splitFullName = (fullName = '') => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

const buildCustomerPayload = (customerInfo, extras = {}) => {
  const firstName = customerInfo.firstName?.trim() || '';
  const lastName = customerInfo.lastName?.trim() || '';
  const fullName = `${firstName} ${lastName}`.trim() || customerInfo.fullName?.trim() || '';
  const streetLine = customerInfo.streetLine?.trim() || '';
  const postcode = customerInfo.postcode?.trim() || '';
  const city = customerInfo.city?.trim() || '';
  const address = [streetLine, `${postcode} ${city}`.trim()].filter(Boolean).join(', ');
  return {
    ...customerInfo,
    fullName,
    firstName,
    lastName,
    streetLine,
    postcode,
    city,
    address,
    lat: customerInfo.lat ?? null,
    lng: customerInfo.lng ?? null,
    location: {
      lat: customerInfo.lat ?? null,
      lng: customerInfo.lng ?? null,
    },
    ...extras,
  };
};

const CustomerInfoStep = ({ user, profile, data, currentStep, onUpdate, onNext, onBack, isActive = true, showBack = true }) => {
  const navigate = useNavigate();
  const isLoggedIn = Boolean(user?.id);
  const [customerInfo, setCustomerInfo] = useState(() => {
    const fullName = getFullNameFromData(data);
    const split = splitFullName(fullName);
    return {
      firstName: data?.firstName || split.firstName || '',
      lastName: data?.lastName || split.lastName || '',
      fullName,
      email: data?.email || '',
      phone: data?.phone || '',
      streetLine: data?.streetLine || '',
      addressComplement: data?.addressComplement || '',
      accessInfo: data?.accessInfo || '',
      postcode: data?.postcode || '',
      city: data?.city || '',
      lat: data?.lat || null,
      lng: data?.lng || null,
      customerType: data?.customerType || 'individual',
      companyName: data?.companyName || '',
      siret: data?.siret || '',
    };
  });

  const [acceptTerms, setAcceptTerms] = useState(data?.acceptTerms ?? false);
  const [acceptRGPD, setAcceptRGPD] = useState(data?.acceptRGPD ?? false);
  const [createAccount, setCreateAccount] = useState(isLoggedIn ? false : (data?.createAccount ?? false));
  const [errors, setErrors] = useState({});
  const [allowedZones, setAllowedZones] = useState([]);
  const [isServiceZonesModalOpen, setIsServiceZonesModalOpen] = useState(false);
  const [isLoadingZones, setIsLoadingZones] = useState(true);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [showEmailExistsModal, setShowEmailExistsModal] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const fieldRefs = useRef({});

  const setFieldRef = (field) => (el) => {
    fieldRefs.current[field] = el;
  };

  useEffect(() => {
    if (!isLoggedIn) return;

    const loadProfileData = async () => {
      setIsLoadingProfile(true);
      try {
        const prefill = await customerPortalService.getBookingPrefillData(user, profile);
        if (!prefill) return;

        setCustomerInfo((prev) => {
          const nextFullName = prev.fullName || prefill.fullName || getFullNameFromData(prefill);
          const split = splitFullName(nextFullName);
          return {
            ...prev,
            fullName: nextFullName,
            firstName: prev.firstName || prefill.firstName || split.firstName,
            lastName: prev.lastName || prefill.lastName || split.lastName,
            email: prev.email || prefill.email,
            phone: prev.phone || prefill.phone,
            streetLine: prev.streetLine || prefill.streetLine,
            postcode: prev.postcode || prefill.postcode,
            city: prev.city || prefill.city,
            lat: prev.lat || prefill.lat,
            lng: prev.lng || prefill.lng,
            accessInfo: prev.accessInfo || prefill.accessInfo,
          };
        });

        onUpdate(customerPortalService.mergeBookingPrefill(data, prefill));
      } catch (err) {
        console.error('Erreur chargement profil client:', err);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfileData();
  }, [user?.id, profile?.id]);

  useEffect(() => {
    setCustomerInfo((prev) => {
      const nextFullName = prev.fullName || getFullNameFromData(data);
      const split = splitFullName(nextFullName);
      return {
        ...prev,
        fullName: nextFullName,
        firstName: prev.firstName || data?.firstName || split.firstName,
        lastName: prev.lastName || data?.lastName || split.lastName,
        email: prev.email || data?.email || '',
        phone: prev.phone || data?.phone || '',
        streetLine: prev.streetLine || data?.streetLine || '',
        postcode: prev.postcode || data?.postcode || '',
        city: prev.city || data?.city || '',
        lat: prev.lat || data?.lat || null,
        lng: prev.lng || data?.lng || null,
        accessInfo: prev.accessInfo || data?.accessInfo || '',
      };
    });
  }, [
    data?.fullName,
    data?.firstName,
    data?.lastName,
    data?.email,
    data?.phone,
    data?.streetLine,
    data?.postcode,
    data?.city,
    data?.lat,
    data?.lng,
    data?.accessInfo,
  ]);

  useEffect(() => {
    const loadZones = async () => {
      setIsLoadingZones(true);
      const zones = await fetchAllowedPostcodes();
      setAllowedZones(zones);
      setIsLoadingZones(false);
    };
    loadZones();
  }, []);

  const ORDERED_FIELDS = [
    'firstName', 'lastName', 'email', 'phone',
    'streetLine', 'postcode', 'city',
    'companyName', 'siret',
    'terms', 'rgpd',
  ];

  const getFieldError = (field, info = customerInfo) => {
    switch (field) {
      case 'firstName':
        if (!info.firstName?.trim()) return 'Le prénom est requis';
        if (info.firstName.trim().length < 2) return 'Le prénom doit contenir au moins 2 caractères';
        return '';
      case 'lastName':
        if (!info.lastName?.trim()) return 'Le nom est requis';
        if (info.lastName.trim().length < 2) return 'Le nom doit contenir au moins 2 caractères';
        return '';
      case 'email':
        if (!info.email?.trim()) return "L'email est requis";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(info.email)) return "Format d'email invalide (ex: nom@domaine.fr)";
        return '';
      case 'phone':
        if (!info.phone?.trim()) return 'Le téléphone est requis';
        if (!/^0[1-9][0-9]{8}$/.test(info.phone.replace(/\s/g, ''))) {
          return 'Numéro invalide (format: 06 12 34 56 78)';
        }
        return '';
      case 'streetLine':
        if (!info.streetLine?.trim()) return 'Le numéro et type de voie sont requis';
        return '';
      case 'postcode': {
        if (!info.postcode?.trim()) return 'Le code postal est requis';
        if (!/^\d{5}$/.test(info.postcode.trim())) return 'Le code postal doit comporter 5 chiffres';
        if (
          allowedZones.length > 0 &&
          !isPostcodeInServiceZones(
            info.postcode.trim(),
            allowedZones.map((zone) => zone.postal_code).filter(Boolean)
          )
        ) {
          return "Ce code postal n'est pas desservi par nos services";
        }
        return '';
      }
      case 'city':
        if (!info.city?.trim()) return 'La ville est requise';
        return '';
      case 'companyName':
        if (info.customerType !== 'business') return '';
        if (!info.companyName?.trim()) return 'La raison sociale est requise';
        if (info.companyName.trim().length < 2) return 'La raison sociale doit contenir au moins 2 caractères';
        return '';
      case 'siret': {
        if (info.customerType !== 'business') return '';
        const siretDigits = (info.siret || '').replace(/\s/g, '');
        if (!siretDigits) return 'Le numéro SIRET est requis';
        if (!/^\d{14}$/.test(siretDigits)) return 'Le SIRET doit comporter 14 chiffres';
        return '';
      }
      case 'terms':
        return acceptTerms ? '' : 'Vous devez accepter les conditions générales';
      case 'rgpd':
        return acceptRGPD ? '' : 'Vous devez accepter la politique de confidentialité';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    ORDERED_FIELDS.forEach((field) => {
      const message = getFieldError(field);
      if (message) newErrors[field] = message;
    });
    return newErrors;
  };

  const setFieldError = (field, message) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  };

  const validateAndShowField = (field, info = customerInfo) => {
    const message = getFieldError(field, info);
    setFieldError(field, message || null);
    return !message;
  };

  const fieldHighlightClass = (field) =>
    errors[field] ? FIELD_ERROR_CLASS : undefined;

  const handleChange = (field, value) => {
    setCustomerInfo((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'firstName' || field === 'lastName') {
        next.fullName = `${field === 'firstName' ? value : prev.firstName || ''} ${field === 'lastName' ? value : prev.lastName || ''}`.trim();
      }
      if (field === 'streetLine' && value.trim() !== (prev.streetLine || '').trim()) {
        next.postcode = '';
        next.lat = null;
        next.lng = null;
      }
      return next;
    });

    if (field === 'postcode') {
      const nextInfo = { ...customerInfo, postcode: value };
      if (value.length === 5 || touchedFields.postcode) {
        validateAndShowField('postcode', nextInfo);
      } else if (errors.postcode) {
        setFieldError('postcode', null);
      }
      return;
    }

    if (touchedFields[field] || errors[field]) {
      const nextInfo = { ...customerInfo, [field]: value };
      validateAndShowField(field, nextInfo);
    } else if (errors[field]) {
      setFieldError(field, null);
    }
  };

  const handleFieldBlur = (field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
    validateAndShowField(field);
  };

  const handleAddressSelect = ({ streetLine, postcode, city, lat, lng }) => {
    const nextInfo = { ...customerInfo, streetLine, postcode, city, lat, lng };
    setCustomerInfo(nextInfo);
    setTouchedFields((prev) => ({ ...prev, streetLine: true, postcode: true, city: true }));
    ['streetLine', 'postcode', 'city'].forEach((field) => validateAndShowField(field, nextInfo));
  };

  useEffect(() => {
    if (isLoadingZones || !customerInfo.postcode?.trim()) return;
    if (customerInfo.postcode.trim().length !== 5) return;
    validateAndShowField('postcode');
  }, [isLoadingZones, allowedZones, customerInfo.postcode]);

  const handleBack = () => {
    // Devis : retour simple vers l’étape précédente (infos complémentaires)
    if (isQuoteService(data?.service)) {
      onBack(true);
      return;
    }

    // Non prix fixe avec priorité/créneau : revenir aux infos complémentaires
    if (!isFixedPriceService(data?.service)) {
      onBack(true, { skipToStep: 4 });
      return;
    }

    onBack(true);
  };

  const submitLabel = getCustomerInfoStepSubmitLabel(data?.service);
  const quoteService = isQuoteService(data?.service);

  const proceedToNextStep = (withCreateAccount = createAccount) => {
    onUpdate(buildCustomerPayload(customerInfo, {
      acceptTerms: true,
      acceptRGPD: true,
      createAccount: isLoggedIn ? false : withCreateAccount,
    }));
    onNext(true);
  };

  const handleLoginRedirect = () => {
    onUpdate(buildCustomerPayload(customerInfo, {
      acceptTerms,
      acceptRGPD,
      createAccount: false,
    }));
    const params = new URLSearchParams({
      returnTo: '/service-booking-wizard',
      step: String(currentStep ?? 7),
    });
    if (customerInfo.email?.trim()) {
      params.set('email', customerInfo.email.trim());
    }
    navigate(`/login-authentication?${params.toString()}`);
  };

  const handleContinueWithoutAccount = () => {
    setCreateAccount(false);
    setShowEmailExistsModal(false);
    proceedToNextStep(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      const firstErrorField = ORDERED_FIELDS.find((f) => newErrors[f]);
      if (firstErrorField) {
        const ref = fieldRefs.current[firstErrorField];
        if (ref) {
          ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = ref.querySelector('input, textarea');
          if (input) input.focus();
        }
      }
      return;
    }

    if (!isLoggedIn && createAccount) {
      const emailExists = await customerPortalService.checkEmailExists(customerInfo.email);
      if (emailExists) {
        setShowEmailExistsModal(true);
        return;
      }
    }

    setErrors({});
    proceedToNextStep();
  };

  const isFormComplete = Object.keys(validateForm()).length === 0;

  return (
    <div className="max-w-5xl mx-auto">
      <EmailExistsModal
        isOpen={showEmailExistsModal}
        email={customerInfo.email}
        onLogin={handleLoginRedirect}
        onContinueWithoutAccount={handleContinueWithoutAccount}
        onChangeEmail={() => {
          setShowEmailExistsModal(false);
          fieldRefs.current.email?.querySelector('input')?.focus();
        }}
      />
      <ServiceZonesModal
        isOpen={isServiceZonesModalOpen}
        onClose={() => setIsServiceZonesModalOpen(false)}
        zones={allowedZones}
      />

      <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
        <div className="flex items-start gap-4  ">
          
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              {isLoggedIn ? 'Vos coordonnées' : 'Veuillez entrer vos coordonnées'}
            </h2>
            <p className="text-muted-foreground">
              {isLoggedIn
                ? 'Vos informations de profil ont été préremplies. Vous pouvez les modifier si nécessaire.'
                : 'Vos coordonnées restent confidentielles et sont utilisées uniquement pour votre intervention.'}
            </p>
          </div>
        </div>

        {isLoggedIn ? (
          <div className="mb-6 p-4 rounded-lg border border-primary/20 bg-primary/5 flex items-start gap-3">
            <Icon name="UserCheck" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">
                Connecté en tant que {profile?.full_name || user?.email}
              </p>
              <p className="text-sm text-muted-foreground">
                {isLoadingProfile
                  ? 'Chargement de vos informations...'
                  : 'Vos données proviennent de votre compte client.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="mb-6 p-4 rounded-lg border border-border bg-muted/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-foreground">Déjà un compte ?</p>
              <p className="text-sm text-muted-foreground">
                Connectez-vous pour préremplir vos coordonnées et reprendre la réservation à cette étape.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              iconName="LogIn"
              iconPosition="left"
              onClick={handleLoginRedirect}
              className="flex-shrink-0"
            >
              Se connecter
            </Button>
          </div>
        )}
 {/* <StepActions isActive={isActive} className="flex justify-between pt-6 pb-6 sm:pt-8">
                    <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={handleBack}>
                      Retour
                    </Button>
                  </StepActions> */}
                         <div className="flex justify-between pt-6  border-t border-border">
</div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Vous êtes</label>
            <div className="flex gap-3">
              {[
                { value: 'individual', icon: 'User', label: 'Particulier' },
                { value: 'business', icon: 'Building', label: 'Professionnel' },
              ].map(({ value, icon, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleChange('customerType', value)}
                  className={`px-4 py-2 rounded-lg border transition-all duration-200 flex items-center gap-2 ${
                    customerInfo.customerType === value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/50 text-foreground'
                  }`}
                >
                  <Icon
                    name={icon}
                    size={18}
                    color={customerInfo.customerType === value ? 'var(--color-primary)' : 'currentColor'}
                  />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {customerInfo.customerType === 'business' && (
            <div className="space-y-4 p-4 rounded-lg border-2 border-border bg-muted/20">
              <div ref={setFieldRef('companyName')}>
                <Input
                  label="Raison sociale"
                  type="text"
                  placeholder="Ex: SARL Dupont Plomberie"
                  description="Nom officiel de l'entreprise"
                  value={customerInfo.companyName}
                  onChange={(e) => handleChange('companyName', e.target.value)}
                  onBlur={() => handleFieldBlur('companyName')}
                  error={errors.companyName}
                  required
                  className={fieldHighlightClass('companyName')}
                />
              </div>
              <div ref={setFieldRef('siret')}>
                <Input
                  label="Numéro SIRET"
                  type="text"
                  placeholder="123 456 789 00012"
                  description="14 chiffres (espaces autorisés)"
                  value={customerInfo.siret}
                  onChange={(e) => handleChange('siret', e.target.value)}
                  onBlur={() => handleFieldBlur('siret')}
                  error={errors.siret}
                  required
                  className={fieldHighlightClass('siret')}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div ref={setFieldRef('firstName')}>
              <Input
                label={customerInfo.customerType === 'business' ? 'Prénom du contact' : 'Prénom'}
                type="text"
                placeholder="Jean"
                value={customerInfo.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                onBlur={() => handleFieldBlur('firstName')}
                error={errors.firstName}
                required
                className={fieldHighlightClass('firstName')}
              />
            </div>
            <div ref={setFieldRef('lastName')}>
              <Input
                label={customerInfo.customerType === 'business' ? 'Nom du contact' : 'Nom'}
                type="text"
                placeholder="Dupont"
                value={customerInfo.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                onBlur={() => handleFieldBlur('lastName')}
                error={errors.lastName}
                required
                className={fieldHighlightClass('lastName')}
              />
            </div>
          </div>

          <div ref={setFieldRef('email')}>
            <Input
              label="Email"
              type="email"
              placeholder="jean.dupont@example.com"
              description="Pour recevoir la confirmation et le suivi"
              value={customerInfo.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleFieldBlur('email')}
              error={errors.email}
              required
              className={fieldHighlightClass('email')}
            />
          </div>

          <div ref={setFieldRef('phone')}>
            <Input
              label="Téléphone"
              type="tel"
              placeholder="06 12 34 56 78"
              description="Pour vous contacter si nécessaire"
              value={customerInfo.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              onBlur={() => handleFieldBlur('phone')}
              error={errors.phone}
              required
              className={fieldHighlightClass('phone')}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground">Adresse d'intervention</p>
              {/* {!isLoadingZones && allowedZones.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsServiceZonesModalOpen(true)}
                  className="text-xs text-primary hover:underline"
                >
                  Zones desservies
                </button>
              )} */}
            </div>

            <div ref={setFieldRef('streetLine')}>
              <AddressAutocomplete
                value={customerInfo.streetLine}
                onChange={(val) => handleChange('streetLine', val)}
                onSelect={handleAddressSelect}
                onBlur={() => handleFieldBlur('streetLine')}
                error={errors.streetLine}
                highlightError={Boolean(errors.streetLine)}
                allowedZones={allowedZones}
                contextCity={customerInfo.city}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div ref={setFieldRef('postcode')} className="sm:col-span-1">
                <Input
                  label="Code postal"
                  type="text"
                  placeholder="75001"
                  maxLength={5}
                  value={customerInfo.postcode}
                  onChange={(e) =>
                    handleChange('postcode', e.target.value.replace(/\D/g, '').slice(0, 5))
                  }
                  onBlur={() => handleFieldBlur('postcode')}
                  error={errors.postcode}
                  required
                  className={fieldHighlightClass('postcode')}
                />
              </div>
              <div ref={setFieldRef('city')} className="sm:col-span-2">
                <Input
                  label="Ville"
                  type="text"
                  placeholder="Paris"
                  value={customerInfo.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  onBlur={() => handleFieldBlur('city')}
                  error={errors.city}
                  required
                  className={fieldHighlightClass('city')}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Informations d'accès
              </label>
              <textarea
                placeholder="Digicode, étage, numéro d'appartement, bâtiment, consignes particulières…"
                className="w-full px-4 py-3 border rounded-lg bg-card text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 border-border placeholder:text-gray-300 focus:placeholder:text-transparent"
                value={customerInfo.accessInfo}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 250) {
                    handleChange('accessInfo', value);
                  }
                }}
                rows={3}
                maxLength={250}
              />
              <div className="flex justify-end mt-1">
                <p className={`text-xs ${
                  customerInfo.accessInfo?.length >= 250 ? 'text-error' : 'text-muted-foreground'
                }`}>
                  {customerInfo.accessInfo?.length || 0}/250
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t border-border">
            <div
              ref={setFieldRef('terms')}
              className={cn(errors.terms && 'rounded-lg border-2 border-orange-500 p-3')}
            >
              <Checkbox
                label="J'accepte les conditions générales de vente"
                checked={acceptTerms}
                onChange={(e) => {
                  setAcceptTerms(e.target.checked);
                  if (e.target.checked) setFieldError('terms', null);
                }}
                error={errors.terms}
              />
            </div>
            <div
              ref={setFieldRef('rgpd')}
              className={cn(errors.rgpd && 'rounded-lg border-2 border-orange-500 p-3')}
            >
              <Checkbox
                label="J'accepte la politique de confidentialité (RGPD)"
                description="Vos données ne sont partagées qu'avec votre accord"
                checked={acceptRGPD}
                onChange={(e) => {
                  setAcceptRGPD(e.target.checked);
                  if (e.target.checked) setFieldError('rgpd', null);
                }}
                error={errors.rgpd}
              />
            </div>
            {!isLoggedIn && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2">
                <Checkbox
                  label="Créer un compte pour suivre mes interventions"
                  description="Optionnel - Accédez à votre historique et documents"
                  checked={createAccount}
                  onChange={(e) => setCreateAccount(e.target.checked)}
                />
                {/* <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconName="LogIn"
                  iconPosition="left"
                  onClick={handleLoginRedirect}
                  className="flex-shrink-0 self-start sm:self-center"
                >
                  J&apos;ai déjà un compte
                </Button> */}
              </div>
            )}
          </div>

          <StepActions isActive={isActive} showBack={showBack} className="flex justify-between pt-8 border-t border-border">
            <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={handleBack} type="button">
              Retour
            </Button>
            <Button
              type="submit"
              variant="default"
              size="lg"
              iconName={quoteService ? 'Send' : 'ArrowRight'}
              iconPosition="right"
              disabled={!isFormComplete}
            >
              {submitLabel}
            </Button>
          </StepActions>
        </form>
      </div>
    </div>
  );
};

export default CustomerInfoStep;